import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useTenant } from "@/context/tenant-context";
import { apiFetch } from "@/api/client";
import { tenantOnboardApi, type OnboardResult } from "@/api/tenant-onboard";
import {
  Shield,
  Power,
  RotateCcw,
  CheckCircle2,
  Loader2,
  Users,
} from "lucide-react";

interface AuthProvider {
  id: string;
  provider: string;
  enabled: boolean;
  enforceSignIn: boolean;
  allowLocalFallback: boolean;
  config: Record<string, unknown> | null;
}

export function TenantAuthSettings() {
  const { tenantId } = useTenant();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();

  const [entraConnecting, setEntraConnecting] = useState(false);
  const [entraOnboardError, setEntraOnboardError] = useState("");
  const [entraOnboardResult, setEntraOnboardResult] = useState<OnboardResult | null>(null);

  // Handle OAuth callback return
  useEffect(() => {
    const token = searchParams.get("onboardComplete");
    const onboardError = searchParams.get("onboardError");
    if (token) {
      searchParams.delete("onboardComplete");
      searchParams.delete("userFetchError");
      setSearchParams(searchParams, { replace: true });

      setEntraConnecting(true);
      tenantOnboardApi
        .getResult(token)
        .then(async (result) => {
          setEntraOnboardResult(result);
          await apiFetch(`/companies/${tenantId}/auth-providers/entra`, {
            method: "PUT",
            body: JSON.stringify({
              enabled: true,
              providerConfig: { tenantId: result.tenantId },
            }),
          });
          queryClient.invalidateQueries({ queryKey: ["auth-providers", tenantId] });
        })
        .catch((err) =>
          setEntraOnboardError(err instanceof Error ? err.message : "Failed to fetch onboard result"),
        )
        .finally(() => setEntraConnecting(false));
    } else if (onboardError) {
      searchParams.delete("onboardError");
      setSearchParams(searchParams, { replace: true });
      setEntraOnboardError(decodeURIComponent(onboardError));
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const { data: authProviders = [] } = useQuery({
    queryKey: ["auth-providers", tenantId],
    queryFn: () => apiFetch<AuthProvider[]>(`/companies/${tenantId}/auth-providers`),
  });

  const activateMutation = useMutation({
    mutationFn: ({ provider, enabled, enforceSignIn }: { provider: string; enabled: boolean; enforceSignIn?: boolean }) =>
      apiFetch(`/companies/${tenantId}/auth-providers/${provider}/activation`, {
        method: "PATCH",
        body: JSON.stringify({ enabled, enforceSignIn }),
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["auth-providers", tenantId] }),
  });

  const rollbackMutation = useMutation({
    mutationFn: () => apiFetch(`/companies/${tenantId}/auth-rollback`, { method: "POST" }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["auth-providers", tenantId] }),
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between text-base">
          <span className="flex items-center gap-2">
            <Shield size={18} />
            Authentication Providers ({authProviders.length})
          </span>
          {authProviders.some((p) => p.enforceSignIn) && (
            <Button
              size="sm"
              variant="outline"
              disabled={rollbackMutation.isPending}
              onClick={() => rollbackMutation.mutate()}
            >
              <RotateCcw size={14} className="mr-1.5" />
              Rollback
            </Button>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {authProviders.length === 0 ? (
          <p className="text-sm text-text-muted">No auth providers configured. Tenants use platform auth by default.</p>
        ) : (
          <div className="flex flex-col gap-2">
            {authProviders.map((ap) => (
              <div key={ap.id} className="flex items-center justify-between rounded px-2 py-2 hover:bg-surface-hover">
                <div className="flex items-center gap-2">
                  <Shield size={14} className="text-accent" />
                  <span className="text-sm font-medium text-text-primary capitalize">{ap.provider}</span>
                </div>
                <div className="flex items-center gap-2">
                  {ap.enforceSignIn && <Badge variant="warning">enforced</Badge>}
                  <Badge variant={ap.enabled ? "success" : "default"}>
                    {ap.enabled ? "active" : "inactive"}
                  </Badge>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() =>
                      activateMutation.mutate({ provider: ap.provider, enabled: !ap.enabled })
                    }
                  >
                    <Power size={14} />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Entra ID connect CTA */}
        {!authProviders.some((p) => p.provider === "entra") && (
          <div className="mt-4 rounded-lg border border-dashed border-border p-4">
            {entraOnboardResult ? (
              <div className="flex items-center gap-3">
                <CheckCircle2 size={20} className="text-green-500 shrink-0" />
                <div>
                  <p className="text-sm font-medium text-text-primary">Microsoft Entra ID connected</p>
                  <p className="text-xs text-text-muted mt-0.5">
                    Tenant ID:{" "}
                    <code className="rounded bg-background px-1 py-0.5 font-mono text-accent">
                      {entraOnboardResult.tenantId}
                    </code>
                  </p>
                  {entraOnboardResult.users.length > 0 && (
                    <p className="text-xs text-text-muted mt-1">
                      <Users size={12} className="inline mr-1 -mt-0.5" />
                      {entraOnboardResult.users.length} user(s) found in directory. Import them from the User Management page.
                    </p>
                  )}
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Shield size={20} className="text-text-muted" />
                  <div>
                    <p className="text-sm font-medium text-text-primary">Microsoft Entra ID</p>
                    <p className="text-xs text-text-muted">Connect to the customer's Azure AD for SSO</p>
                  </div>
                </div>
                <Button
                  size="sm"
                  disabled={entraConnecting}
                  onClick={async () => {
                    setEntraConnecting(true);
                    setEntraOnboardError("");
                    try {
                      const { consentUrl } = await tenantOnboardApi.startOnboard(tenantId);
                      window.open(consentUrl, "_blank");
                    } catch (err) {
                      setEntraOnboardError(
                        err instanceof Error ? err.message : "Failed to start onboarding",
                      );
                    } finally {
                      setEntraConnecting(false);
                    }
                  }}
                >
                  {entraConnecting ? (
                    <Loader2 size={14} className="mr-1.5 animate-spin" />
                  ) : (
                    <Shield size={14} className="mr-1.5" />
                  )}
                  Connect Entra ID
                </Button>
              </div>
            )}
            {entraOnboardError && <p className="mt-2 text-sm text-red-500">{entraOnboardError}</p>}
            {!entraOnboardResult && (
              <p className="mt-2 text-xs text-text-muted">
                A new tab will open for the customer admin to sign in and grant consent. Once complete, the Entra provider will be automatically configured.
              </p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
